// src/api/microsite.ts
//
// Authenticated API functions for microsite management.
// Used by the main BandJerry app (builder, management, analytics).
// Public (unauthenticated) functions are in micrositePublic.ts.

import api from './index';
import type {
  MicrositePage,
  MicrositeFormState,
  MicrositeAnalytics,
  MicrositeUploadUrlResponse,
  TrackableUrl,
} from '@/types/microsite';

// --- Authenticated endpoints ---

export async function getMicrositePage(): Promise<{ page: MicrositePage }> {
  const response = await api.get('/microsite');
  return response.data;
}

export async function createMicrositePage(
  data: MicrositeFormState,
): Promise<{ page: MicrositePage }> {
  const response = await api.post('/microsite', data);
  return response.data;
}

export async function updateMicrositePage(
  data: Partial<MicrositeFormState>,
): Promise<{ page: MicrositePage }> {
  const response = await api.patch('/microsite', data);
  return response.data;
}

export async function deleteMicrositePage(): Promise<void> {
  await api.delete('/microsite');
}

export async function getMicrositeUploadUrl(
  filename: string,
  contentType: string,
): Promise<MicrositeUploadUrlResponse> {
  const response = await api.post('/microsite/upload-url', {
    filename,
    content_type: contentType,
  });
  return response.data;
}

export async function getMicrositeAnalytics(
  range: '7d' | '30d' = '30d',
): Promise<MicrositeAnalytics> {
  const response = await api.get('/microsite/analytics', { params: { range } });
  return response.data;
}

export async function exportMicrositeSignups(): Promise<Blob> {
  const response = await api.get('/microsite/signups/export', {
    responseType: 'blob',
  });
  return response.data;
}

export async function getMicrositeSignupsEmailList(): Promise<string> {
  const response = await api.get('/microsite/signups/email-list', {
    responseType: 'text',
  });
  return response.data;
}

// --- Trackable URL endpoints ---

export async function createMicrositeTrackableUrl(
  name: string,
): Promise<{ trackable_url: TrackableUrl }> {
  const response = await api.post('/microsite/trackable-urls', { name });
  return response.data;
}

export async function deleteMicrositeTrackableUrl(
  trackingId: string,
): Promise<void> {
  await api.delete(`/microsite/trackable-urls/${trackingId}`);
}
